import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';

import { Icon } from '~/modules/elements/akui/icon';
import events from '~/modules/game-events/game-events';
import { useEventEffect } from '~/modules/game-events/hooks';
import { serverRpc } from '~/modules/remote-mic/network/client';
import { useSubscription } from '~/modules/remote-mic/network/client/hooks/use-subscription';
import storage from '~/modules/utils/storage';
import vibrate from '~/modules/utils/vibrate';

interface Props {
  onConfirm: () => void;
}

// What the game waits before starting without this phone, when a host too old to send it does not.
// Kept in step with `AUTOSTART_TIMEOUT_S` by the request carrying the real figure - this is only the
// floor under a version mismatch.
const FALLBACK_SECONDS = 15;

// The ring is drawn on a fixed viewBox and scaled by the box around it, so one set of numbers serves
// every screen size. 691 is the circumference of r=110, which is what the dash array has to be for
// the arc to read as a fraction of the whole.
const RING_CIRCUMFERENCE = 691;

function ConfirmReadiness({ onConfirm }: Props) {
  const [visible, setVisible] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [totalSeconds, setTotalSeconds] = useState(FALLBACK_SECONDS);
  const [remaining, setRemaining] = useState(FALLBACK_SECONDS);
  const [isAfterReload, setIsAfterReload] = useState(false);

  const currentSong = useSubscription('current-song');

  const open = (secondsLeft?: number) => {
    const seconds = secondsLeft ?? FALLBACK_SECONDS;
    setTotalSeconds(seconds);
    setRemaining(seconds);
    setConfirmed(false);
    setVisible(true);
  };

  // The game asks for readiness after a reload it requested, and the ask can arrive before this
  // phone has a slot - so a phone that has just been given one is shown the prompt on that basis.
  useEffect(() => {
    if (storage.session.getItem('reload-mic-request') !== null) {
      setIsAfterReload(true);
    }
  }, []);
  useEventEffect(events.remoteMicPlayerSet, () => {
    if (isAfterReload) {
      setIsAfterReload(false);
      open();
    }
  });

  useEventEffect(events.remoteReadinessRequested, (secondsLeft) => open(secondsLeft));

  // The one thing that reaches a phone lying face down. Stopped on the tap rather than on the
  // overlay leaving, because after the tap there is nothing left to answer.
  useEffect(() => {
    if (!visible || confirmed) {
      vibrate(0);
      return;
    }

    vibrate([100, 900, 100, 900, 100]);
    const intervalId = setInterval(() => vibrate([100, 900, 100, 900, 100]), 3000);

    return () => {
      clearInterval(intervalId);
      vibrate(0);
    };
  }, [visible, confirmed]);

  useEffect(() => {
    if (!visible) return;

    const intervalId = setInterval(() => setRemaining((current) => Math.max(0, current - 1)), 1000);

    return () => clearInterval(intervalId);
  }, [visible]);

  // Two ways out, and neither is the button. The song starting is the real one - it closes the
  // prompt for somebody who never answered it, which is the whole point. The count reaching zero is
  // the backstop for a host that never tells us, since the game starts at zero regardless.
  const songStarted = currentSong?.started === true;
  useEffect(() => {
    if (visible && (songStarted || remaining === 0)) {
      setVisible(false);
    }
  }, [visible, songStarted, remaining]);

  const confirm = () => {
    void serverRpc.input.confirmReadiness();
    setConfirmed(true);
    onConfirm();
  };

  // The arc drains once, anticlockwise from the top, rather than repeating: it says how long is
  // left instead of just moving.
  const swept = totalSeconds > 0 ? 1 - remaining / totalSeconds : 1;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-test="readiness-prompt"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          // Opaque, over the ribbon and the tab bar both: nothing else on the phone matters until
          // this is answered, and there is nothing behind it worth showing through.
          className="fixed inset-0 z-[1000] flex flex-col bg-[#05070b] text-white">
          <div className="flex min-h-0 flex-1 flex-col items-center gap-6.5 px-6.5 pt-7.5 text-center">
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={confirmed ? 'sent' : 'asking'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.16 }}
                className="text-[1.375rem] leading-[1.25] font-bold text-pretty">
                {confirmed ? 'Waiting for the others' : 'The game is waiting on you'}
              </motion.span>
            </AnimatePresence>

            {currentSong && (
              <span className="flex flex-col gap-0.5">
                <span className="text-active text-[1.125rem] font-bold">{currentSong.title}</span>
                <span className="text-[0.9375rem] text-white/70">{currentSong.artist}</span>
              </span>
            )}

            <div className="relative mt-3.5 flex aspect-square w-full max-w-[15.125rem] items-center justify-center">
              <svg viewBox="0 0 260 260" className="absolute inset-0 size-full -rotate-90" aria-hidden>
                <circle cx="130" cy="130" r="110" fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="12" />
                <circle
                  cx="130"
                  cy="130"
                  r="110"
                  fill="none"
                  stroke="rgb(255,165,0)"
                  strokeWidth="12"
                  strokeLinecap="round"
                  strokeDasharray={RING_CIRCUMFERENCE}
                  strokeDashoffset={RING_CIRCUMFERENCE * swept}
                  // Stepping with the number rather than creeping between them, and dropped entirely
                  // for anyone who asked for less motion - the figure still says everything.
                  className="motion-safe:transition-[stroke-dashoffset] motion-safe:duration-1000 motion-safe:ease-linear"
                />
              </svg>
              <span className="flex flex-col items-center gap-0.5">
                <span data-test="ready-countdown" className="text-[4.75rem] leading-none font-bold tabular-nums">
                  {remaining}
                </span>
                <span className="text-[0.8125rem] tracking-[0.08em] text-white/60 uppercase">seconds</span>
              </span>
            </div>
          </div>

          <div className="flex shrink-0 flex-col px-6.5 pb-10.5">
            {confirmed ? (
              <span
                data-test="ready-confirmed"
                className="text-active flex h-17 items-center justify-center gap-3 rounded-[1.25rem] bg-[rgba(255,165,0,0.14)] text-[1.1875rem] font-bold shadow-[inset_0_0_0_2px_rgba(255,165,0,0.55)]">
                <Icon icon="ic:outline-check-circle" size={7.5} />
                You&#39;re ready
              </span>
            ) : (
              <button
                type="button"
                onClick={confirm}
                data-test="ready-button"
                className="bg-active flex h-17 cursor-pointer items-center justify-center gap-3 rounded-[1.25rem] border-none text-[1.1875rem] font-bold text-black transition-transform duration-100 active:scale-[0.98]">
                <Icon icon="ic:baseline-mic" size={7.5} />
                I&#39;m ready
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
export default ConfirmReadiness;
